import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";

const PasswordStrengthMeter = ({ password }) => {
  const [strength, setStrength] = useState(0);
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    number: false,
    special: false,
  });

  useEffect(() => {
    if (!password) {
      setStrength(0);
      setRequirements({
        length: false,
        uppercase: false,
        number: false,
        special: false,
      });
      return;
    }

    const newRequirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password),
    };

    setRequirements(newRequirements);

    // Calculate strength (0-4)
    const score = Object.values(newRequirements).filter(Boolean).length;
    setStrength(score);
  }, [password]);

  const getStrengthColor = () => {
    switch (strength) {
      case 0:
      case 1:
        return "bg-red-500";
      case 2:
        return "bg-orange-500";
      case 3:
        return "bg-yellow-500";
      case 4:
        return "bg-green-500";
      default:
        return "bg-gray-300";
    }
  };

  const getStrengthText = () => {
    switch (strength) {
      case 0:
      case 1:
        return "Weak";
      case 2:
        return "Fair";
      case 3:
        return "Good";
      case 4:
        return "Strong";
      default:
        return "";
    }
  };

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2">
      {/* Strength Bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getStrengthColor()}`}
            style={{ width: `${(strength / 4) * 100}%` }}
          />
        </div>
        <span className={`text-sm font-medium ${
          strength === 4 ? "text-green-600" : 
          strength === 3 ? "text-yellow-600" : 
          strength === 2 ? "text-orange-600" : 
          "text-red-600"
        }`}>
          {getStrengthText()}
        </span>
      </div>

      {/* Requirements Checklist */}
      <div className="text-xs space-y-1">
        <div className={`flex items-center gap-1 ${requirements.length ? "text-green-600" : "text-slate-500"}`}>
          {requirements.length ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          <span>At least 8 characters</span>
        </div>
        <div className={`flex items-center gap-1 ${requirements.uppercase ? "text-green-600" : "text-slate-500"}`}>
          {requirements.uppercase ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          <span>One uppercase letter</span>
        </div>
        <div className={`flex items-center gap-1 ${requirements.number ? "text-green-600" : "text-slate-500"}`}>
          {requirements.number ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          <span>One number</span>
        </div>
        <div className={`flex items-center gap-1 ${requirements.special ? "text-green-600" : "text-slate-500"}`}>
          {requirements.special ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          <span>One special character (!@#$%^&*...)</span>
        </div>
      </div>
    </div>
  );
};

export default PasswordStrengthMeter;
